import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Table,
  Loader,
  Segment,
  Header,
  Input,
  Tab,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi, isTerminologyRestricted } from '../api';

// DR-2026-053: Restricted access message (no hooks — safe for early return)
const AccessRestricted = () => (
  <Container style={{ paddingTop: '2em' }}>
    <Helmet title="Access Restricted - Polyglot" />
    <Link to="/polyglot">&larr; Back to Dashboard</Link>
    <Segment color="red" style={{ marginTop: '1em' }}>
      <Header as="h3">Access Restricted</Header>
      <p>Terminology search is not available for this account.</p>
    </Segment>
  </Container>
);

const GlossarySearchInner = () => {
  const [query, setQuery] = useState('');
  const [language, setLanguage] = useState('');
  const [iateResults, setIateResults] = useState([]);
  const [microsoftResults, setMicrosoftResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  const doSearch = useCallback(() => {
    if (!query || query.length < 2) return;
    setLoading(true);
    setError(null);
    setSearched(true);

    const params = new URLSearchParams({ q: query });
    if (language) params.set('language', language);

    fetchApi(`${getApiPath()}/@polyglot/glossary?${params.toString()}`)
      .then((json) => {
        setIateResults(json.iate || []);
        setMicrosoftResults(json.microsoft || []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [query, language]);

  const panes = [
    {
      menuItem: `IATE (${iateResults.length})`,
      render: () => (
        <Tab.Pane>
          {iateResults.length === 0 ? (
            <p>No IATE results.</p>
          ) : (
            <Table compact celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>English Term</Table.HeaderCell>
                  <Table.HeaderCell>Language</Table.HeaderCell>
                  <Table.HeaderCell>Translation</Table.HeaderCell>
                  <Table.HeaderCell>Source</Table.HeaderCell>
                  <Table.HeaderCell>Reliability</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {iateResults.map((row, i) => (
                  <Table.Row key={i}>
                    <Table.Cell>{row.english_term}</Table.Cell>
                    <Table.Cell>{row.language_code}</Table.Cell>
                    <Table.Cell>{row.translation}</Table.Cell>
                    <Table.Cell>{row.source}</Table.Cell>
                    <Table.Cell>{row.reliability}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </Tab.Pane>
      ),
    },
    {
      menuItem: `Microsoft (${microsoftResults.length})`,
      render: () => (
        <Tab.Pane>
          {microsoftResults.length === 0 ? (
            <p>No Microsoft Terminology results.</p>
          ) : (
            <Table compact celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>English Term</Table.HeaderCell>
                  <Table.HeaderCell>Language</Table.HeaderCell>
                  <Table.HeaderCell>Translation</Table.HeaderCell>
                  <Table.HeaderCell>Definition</Table.HeaderCell>
                  <Table.HeaderCell>Part of Speech</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {microsoftResults.map((row, i) => (
                  <Table.Row key={i}>
                    <Table.Cell>{row.en_term}</Table.Cell>
                    <Table.Cell>{row.language_code}</Table.Cell>
                    <Table.Cell>{row.translation}</Table.Cell>
                    <Table.Cell style={{ maxWidth: '300px', wordBreak: 'break-word' }}>
                      {row.definition}
                    </Table.Cell>
                    <Table.Cell>{row.part_of_speech}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </Tab.Pane>
      ),
    },
  ];

  return (
    <Container id="polyglot-glossary" style={{ paddingTop: '2em', paddingBottom: '2em' }}>
      <Helmet title="Glossary Search - Polyglot" />
      <Link to="/polyglot">&larr; Back to Dashboard</Link>

      <div className="polyglot-page-header">
        <h1>Glossary Search</h1>
        <p className="subtitle">Search across IATE (EU) and Microsoft Terminology</p>
      </div>

      <Segment>
        <div style={{ display: 'flex', gap: '1em', flexWrap: 'wrap', alignItems: 'center' }}>
          <Input
            placeholder="Search term (min 2 chars)..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') doSearch();
            }}
            action={{ icon: 'search', onClick: doSearch }}
            style={{ width: '400px' }}
          />
          <Input
            placeholder="Language (e.g. sv, de)"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            style={{ width: '200px' }}
          />
        </div>
      </Segment>

      {error && <Segment color="red">Error: {error}</Segment>}

      {loading ? (
        <Loader active inline="centered" content="Searching..." />
      ) : (
        searched && <Tab panes={panes} />
      )}

      <p style={{ marginTop: '2em', fontSize: '0.85em', color: '#888' }}>
        IATE terminology from the{' '}
        <a href="https://iate.europa.eu/" target="_blank" rel="noopener noreferrer">
          IATE (Interactive Terminology for Europe)
        </a>{' '}
        database, &copy; European Union.
        Microsoft terminology from the{' '}
        <a
          href="https://learn.microsoft.com/en-us/globalization/reference/microsoft-terminology"
          target="_blank"
          rel="noopener noreferrer"
        >
          Microsoft Terminology Collection
        </a>
        , used under CC BY 4.0.
      </p>
    </Container>
  );
};

// DR-2026-053: Gate component — no hooks before the conditional
const GlossarySearch = () =>
  isTerminologyRestricted() ? <AccessRestricted /> : <GlossarySearchInner />;

export default GlossarySearch;
